import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Trophy, GitBranch, Users, Gift, Search } from 'lucide-react';
import { getTournamentById } from '../../api/wikiApi';
import { Status, Meta, Champion } from './TournamentElements';
import { getWinner, getPrizes, scoreLabel } from './tournamentPresentation';
import styles from './TournamentExperience.module.css';

export function BracketMatch({ match, index }) {
  return <div className={styles.match}>
    <div className={styles.matchLabel}>Матч {String(index + 1).padStart(2, '0')}<span>{match.winner ? 'Завершён' : 'Ожидает результата'}</span></div>
    {[1, 2].map((number) => {
      const name = match[`player${number}`];
      const pending = !name || name === 'TBD';
      const winner = !pending && match.winner === name;
      return <div key={number} className={`${styles.player} ${winner ? styles.playerWinner : ''} ${pending ? styles.playerPending : ''}`}>
        <span className={styles.playerName}>{pending ? 'Участник определится' : <Link to={`/players/${encodeURIComponent(name)}`}>{name}</Link>}</span>
        {winner && <Check size={15} aria-label="Победитель" />}
        <span className={styles.score}>{scoreLabel(match[`score${number}`])}</span>
      </div>;
    })}
  </div>;
}

export function TournamentBracket({ rounds }) {
  const [selected, setSelected] = useState('all');
  return <>
    <div className={styles.bracketToolbar}>
      <p>Победители отмечены галочкой. «—» — счёт не указан.</p>
      <label className={styles.roundSelect}>Показать<select aria-label="Раунд турнирной сетки" value={selected} onChange={(e) => setSelected(e.target.value)}>
        <option value="all">Всю сетку</option>
        {rounds.map((r, i) => <option key={i} value={String(i)}>{r.name || `Раунд ${i + 1}`}</option>)}
      </select></label>
    </div>
    <div className={styles.bracketScroll} tabIndex={0} role="region" aria-label="Турнирная сетка. Прокрутка по горизонтали">
      <div className={`${styles.bracketGrid} ${selected !== 'all' ? styles.singleRound : ''}`}>
        {rounds.map((round, roundIndex) => (selected === 'all' || Number(selected) === roundIndex) && <section key={round.id || roundIndex} className={styles.round}>
          <h3><span>{String(roundIndex + 1).padStart(2, '0')}</span>{round.name || `Раунд ${roundIndex + 1}`}</h3>
          <div className={styles.matches}>{(round.matches || []).map((match, i) => <BracketMatch key={match.id || i} match={match} index={i} />)}
            {!round.matches?.length && <p className={styles.muted}>Матчи ещё не объявлены</p>}
          </div>
        </section>)}
      </div>
    </div>
    {selected === 'all' && rounds.length > 1 && <p className={styles.scrollHint}>Листай сетку по горизонтали или выбери отдельный раунд ↑</p>}
  </>;
}

function TournamentContent({ tournament }) {
  const [search, setSearch] = useState('');
  const prizes = getPrizes(tournament);
  const participants = Array.isArray(tournament.participants) ? tournament.participants : [];
  const rounds = Array.isArray(tournament.rounds) ? tournament.rounds : [];
  const winner = getWinner(tournament);
  const filteredParticipants = participants.map((name, index) => ({ name, index }))
    .filter(({ name }) => name.toLocaleLowerCase('ru').includes(search.trim().toLocaleLowerCase('ru')));
  const sections = [[rounds.length, 'bracket', GitBranch, 'Турнирная сетка'], [prizes.length, 'prizes', Gift, 'Награды'], [participants.length, 'participants', Users, 'Участники']];
  return <main className={`${styles.body} ${styles.detail}`}>
    <Link className={styles.backLink} to="/tournaments"><ArrowLeft size={17} />Все турниры</Link>
    <header className={styles.detailHero}>
      <div className={styles.cardTop}><span className={styles.eyebrow}>InterWorld / Арена</span><Status status={tournament.status} /></div>
      <h1>{tournament.title}</h1>
      <Meta tournament={tournament} />
      {tournament.description && <p className={styles.description}>{tournament.description}</p>}
      <nav className={styles.sectionNav} aria-label="Разделы турнира">{sections.filter(([count]) => count > 0).map(([count, anchor, Icon, label]) =>
        <a href={`#${anchor}`} key={anchor}><Icon size={16} />{label}<span>{count}</span></a>)}</nav>
    </header>
    {winner && <Champion name={winner} />}
    {rounds.length > 0 && <section className={styles.section} id="bracket">
      <div className={styles.sectionHeading}><div><span className={styles.eyebrow}>Путь к победе</span><h2>Турнирная сетка</h2></div><GitBranch size={25} /></div>
      <TournamentBracket rounds={rounds} />
    </section>}
    {prizes.length > 0 && <section className={styles.section} id="prizes">
      <div className={styles.sectionHeading}><div><span className={styles.eyebrow}>Награда за мастерство</span><h2>На кону — больше, чем победа</h2></div><Trophy size={27} /></div>
      <div className={styles.prizes}>{prizes.map((prize, i) => <article key={i} className={`${styles.prize} ${prize.place === 1 ? styles.firstPlace : ''}`}>
        <div className={styles.place}><span>{String(prize.place).padStart(2, '0')}</span><Trophy size={26} /></div>
        <h3>{prize.place} место</h3>
        <ul>{prize.rewards.map((reward, ri) => <li key={ri}><Gift size={14} /><span>{reward}</span></li>)}</ul>
      </article>)}</div>
    </section>}
    {participants.length > 0 && <section className={styles.section} id="participants">
      <div className={styles.sectionHeading}><div><span className={styles.eyebrow}>Лица арены</span><h2>Участники <span className={styles.count}>{participants.length}</span></h2></div>
        <label className={styles.search}><Search size={18} /><input type="search" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Поиск участника" placeholder="Найти участника…" /></label>
      </div>
      <div className={styles.participants}>{filteredParticipants.map(({ name, index }) => <div key={index} className={styles.participant}>
        <span className={styles.participantIndex}>{String(index + 1).padStart(2, '0')}</span><Link to={`/players/${encodeURIComponent(name)}`}>{name}</Link>
      </div>)}</div>
      {!filteredParticipants.length && <p className={styles.muted} role="status">Участник с таким именем не найден.</p>}
    </section>}
    {!rounds.length && !participants.length && !prizes.length && <div className={styles.state}><Trophy size={36} /><h2>Подготовка к турниру</h2><p>Сетка, награды и состав участников появятся здесь позже.</p></div>}
  </main>;
}

export default function TournamentPage() {
  const { id } = useParams();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(null); setTournament(null);
    getTournamentById(id).then((data) => {
      if (active) {
        if (!data || data.status === 'draft') setError('Турнир не найден');
        else setTournament(data);
      }
    }).catch(() => { if (active) setError('Не удалось загрузить турнир'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);
  return <div className={styles.page}>{loading ? <div className={styles.state} role="status"><div className={styles.spinner} />Загрузка турнира…</div>
    : error || !tournament ? <div className={styles.state} role="alert"><Trophy size={36} /><h1>{error || 'Турнир не найден'}</h1><Link to="/tournaments" className={styles.button}>Все турниры</Link></div>
      : <TournamentContent key={id} tournament={tournament} />}</div>;
}
